import styled, { ThemeMedia } from 'styled-components';

type GridTemplateCols = Partial<Record<keyof ThemeMedia | 'base', string | undefined>>;
type BaseGridProps = { gap?: string; onlyChild?: boolean; width?: string };
type GridAlign = 'center' | 'start' | 'end';

const BaseGrid = styled.div<BaseGridProps>`
  width: ${({ width }) => (width ? width : '100%')};
  display: grid;
  grid-template-columns: 1fr;

  ${({ gap }) => gap && `gap: ${gap};`}

  // Use full width if only child.
  ${({ onlyChild }) =>
      onlyChild &&
      `
      > :only-child {
        grid-column: 1/-1; 
      }
  `}
`;

type ExtendedGridProps = {
    columns?: GridTemplateCols;
    align?: GridAlign;
    justify?: GridAlign;
};

/**
 * Simple grid component abstracting away some boilerplate of defining your own.
 *
 * @param columns - A record of breakpoint keys and value is placed in grid-template-columns.
 * @param align - base alignment of items - center | start | end
 * @param justify - base vertical alignment of items - center | start | end
 * @param gap - {string?} grid gap
 * @param width - {string?} set width - defaults to 100%
 * @param onlyChild - {boolean} if true, grid items that are the only child of the grid will
 *                    span all columns. Good for dynamiic grid content where it will look better to use as much space as available.
 */
const Grid = styled(BaseGrid)<ExtendedGridProps>`
    ${({ columns }) => columns?.base && `grid-template-columns: ${columns.base};`}

    ${({ theme }) => theme.media.sm} {
        ${({ columns }) => columns?.sm && `grid-template-columns: ${columns.sm};`}
    }

    ${({ theme }) => theme.media.md} {
        ${({ columns }) => columns?.md && `grid-template-columns: ${columns.md};`}
    }

    ${({ theme }) => theme.media.lg} {
        ${({ columns }) => columns?.lg && `grid-template-columns: ${columns.lg};`}
    }

    ${({ theme }) => theme.media.xl} {
        ${({ columns }) => columns?.xl && `grid-template-columns: ${columns.xl};`}
    }

    align-items: ${({ align }) => align};
    justify-items: ${({ justify }) => justify};
`;

export type GridProps = BaseGridProps & ExtendedGridProps;

/**
 * A two column grid with defined side panel widths.
 */
export const GridSummary = ({ children, gap, ...props }: GridProps & { children: React.ReactNode }) => (
    <Grid {...props} gap={gap} columns={{ base: '1fr', lg: '1fr 300px', xl: '1fr 350px' }}>
        {children}
    </Grid>
);

/**
 * Place child on grid columns and align by attributes
 */
type GridChildProps = { col?: string; justify?: GridAlign; align?: GridAlign };
export const GridChild = styled.div<GridChildProps>`
  ${({ col }) => col && `grid-column: ${col};`}
  ${({ justify }) => justify && `justify-self: ${justify};`}
  ${({ align }) => align && `align-self: ${align};`}
`;

export default Grid;
